import { useEffect, useRef, useState } from "react";

import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { ContentRelationshipField } from "@prismicio/client";
import { KeyTextField } from "@prismicio/types";
import { animated, useSpringRef, useTransition } from "@react-spring/web";
import { useRouter } from "next/router";
import qs from "qs";
import { FormProvider, useForm } from "react-hook-form";

import { PagePrompt, PagePromptProps } from "components/PagePrompt/PagePrompt";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { useBrowserBackInterception } from "hooks/common/useBrowserBackInterception";
import { useSubtotal } from "hooks/product/useSubtotal";
import {
  FormDocumentDataSlicesSlice,
  NavigationWarningModalSlice,
} from "prismicio-types";
import {
  useGetProduct,
  useSetCustomisation,
} from "queries/legacy/product-queries";
import { SSR_CONFIGURATION_ERROR } from "utils/legacy/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/legacy/fallback-content";
import { useGetDefaultValuesFromFormDocuments } from "utils/legacy/form-utils";
import { useInterpolate } from "utils/legacy/prismic-utils";
import { useFindByKey } from "utils/legacy/schema-utils";
import { useGetPathnameForDestination } from "utils/legacy/url-utils";

import { useStatus } from "../../providers/status.context";
import { getPageUpsellPrice } from "../../utils/get-page-upsell-price";

import styles from "./CreationFlow.module.scss";
import CreationFlowStep from "./creationFlowStep/CreationFlowStep";

const isPresentationalStep = (step: {
  muse_schema_key?: KeyTextField;
}): boolean => {
  return (
    !!step.muse_schema_key && step.muse_schema_key.startsWith("presentational_")
  );
};

type FormDocumentType = ContentRelationshipField & {
  data: { slices: FormDocumentDataSlicesSlice[] };
};

export default function CreationFlow() {
  const router = useRouter();
  const transRef = useSpringRef();

  const { data: product } = useGetProduct();
  const { mutate: setCustomisation } = useSetCustomisation();
  const { updateSubtotal } = useSubtotal(product?.museSlug);

  const getPathnameForDestination = useGetPathnameForDestination();
  const findByKey = useFindByKey();
  const interpolate = useInterpolate();
  const [backNavPrompt, setBackNavPrompt] = useState<PagePromptProps | null>(
    null
  );

  const { setStatus } = useStatus();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  // Website product page URL for use on cancel/back
  const productPageUrl = getPathnameForDestination({
    destination: "website",
    path: "/personalized-products/" + router.query.solidus_slug,
  });

  // Only show steps that feature in the schema
  const creationFlowData = product.content.product.creation_flow_steps.filter(
    // The name of this function is the same of a proprietaty testing library
    // function so we we need to disable the eslint rule
    // eslint-disable-next-line testing-library/await-async-query
    ({ muse_schema_key }) =>
      (muse_schema_key && !!findByKey(muse_schema_key)) ||
      isPresentationalStep({ muse_schema_key })
  );

  const methods = useForm({
    // "raw": a value that exists only as an advisory-seeded default (an editions book's
    // page_group) must not pre-fill its step — the field is required, so the customer has to
    // actively choose before Next. Stored/URL values (order edits) still pre-fill. ENG-1715.
    defaultValues: useGetDefaultValuesFromFormDocuments(
      creationFlowData.map((c) => c.form as any),
      { customisationSource: "raw" }
    ),
  });

  const [currentStep, setCurrentStep] = useState(0);

  const previousStep = useRef(-1);

  // Intercept browser back button to prevent leaving creation flow
  useBrowserBackInterception({ currentStep, productPageUrl });

  useEffect(() => {
    const handleBack = (event: Event) => {
      event.preventDefault();

      const { currentStep, productPageUrl } = (event as CustomEvent).detail;

      const step = creationFlowData[currentStep];

      // creationFlowData can be shorter than currentStep while the schema is still loading.
      if (!step) {
        window.location.href = productPageUrl;
        return;
      }

      const form = step.form as FormDocumentType;

      if (form.data) {
        const hasBackNavigationModal = form.data.slices.some(
          (slice) => slice.slice_type === "navigation_warning_modal"
        );

        if (hasBackNavigationModal) {
          const modalSlice = form.data.slices.find(
            (slice): slice is NavigationWarningModalSlice =>
              slice.slice_type === "navigation_warning_modal"
          );

          setBackNavPrompt({
            title:
              modalSlice?.primary.title || "Are you sure you want to go back?",
            content: modalSlice?.primary.content,
            acceptText: modalSlice?.primary.accept_button || "Accept",
            rejectText: modalSlice?.primary.cancel_button || "Cancel",
            onAccept: () => {
              setCustomisation({ behaviour: "regenerate", data: {} });
              setCurrentStep(0);
            },
            onReject: () => setBackNavPrompt(null),
          });
          return;
        }
      }

      window.location.href = productPageUrl;
    };

    window.addEventListener("creationFlowBrowserBack", handleBack);

    return () =>
      window.removeEventListener("creationFlowBrowserBack", handleBack);
  }, [creationFlowData, setBackNavPrompt, setCurrentStep]);

  const transitions = useTransition(currentStep, {
    ref: transRef,
    keys: null,
    immediate: previousStep.current === -1,
    from: () => ({
      opacity: 0,
      transform: `translateX(${
        previousStep.current < currentStep ? 40 : -40
      }%)`,
    }),
    enter: () => ({
      opacity: 1,
      transform: `translateX(0%)`,
    }),
    leave: () => ({
      opacity: 0,
      transform: `translateX(${
        previousStep.current < currentStep ? -40 : 40
      }%)`,
    }),
    config: {
      tension: 220,
      friction: 14,
      clamp: true,
    },
    onRest: () => {
      previousStep.current = currentStep;
    },
  });

  useEffect(() => {
    transRef.start();
  }, [currentStep, transRef]);

  async function onStepSuccess(data: qs.ParsedQs) {
    // Save new customisation to local storage
    setCustomisation({ data });

    if (currentStep === creationFlowData.length - 1) {
      // This covers the selection of the cover before
      // the preview step.
      // Eg. HN book (https://staging.wonderbly.io/personalized-products/newspaper-birthday-book/create/start)
      const coverSelection = data.cover_style;

      if (coverSelection && product) {
        const priceDetail = getPageUpsellPrice(
          { cover_style: coverSelection },
          product
        );

        if (priceDetail) {
          updateSubtotal(priceDetail);
        }
      }

      router.push(getPathnameForDestination({ path: "/create/preview" }));
    } else {
      setCurrentStep(currentStep + 1);
      setStatus("idle");
    }
  }

  function onBackClicked() {
    if (currentStep === 0) {
      window.location.href = productPageUrl;
    } else {
      setCurrentStep(currentStep - 1);
    }
  }

  if (!creationFlowData[currentStep]) {
    return null;
  }

  return (
    <div className={styles.creationFlow}>
      <header className={styles.header}>
        <h1 className={styles.headerTitle}>
          {product.content.product.muse_product_name}
        </h1>

        <a
          className={styles.closeButton}
          href={productPageUrl}
          aria-label={interpolate(
            product.content.canvas.back_product_page_link ??
              CANVAS_FALLBACK_CONTENT.back_product_page_link
          )}
        >
          <XMarkIcon />
        </a>
      </header>

      <FormProvider {...methods}>
        <main className={styles.step}>
          {transitions((style, i) => (
            <animated.div className={styles.stepInner} style={style}>
              <ErrorBoundary>
                <CreationFlowStep
                  currentStep={i + 1}
                  data={creationFlowData[i]}
                  numberOfSteps={creationFlowData.length}
                  onBack={onBackClicked}
                  onSuccess={onStepSuccess}
                />
              </ErrorBoundary>
            </animated.div>
          ))}
        </main>

        {backNavPrompt && (
          <PagePrompt
            acceptText={backNavPrompt.acceptText}
            content={backNavPrompt.content}
            rejectText={backNavPrompt.rejectText}
            title={backNavPrompt.title}
            onAccept={() => {
              backNavPrompt.onAccept();
              setBackNavPrompt(null);
            }}
            onReject={() => {
              backNavPrompt.onReject();
              setBackNavPrompt(null);
            }}
          />
        )}
      </FormProvider>

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style global jsx>{`
        body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
}
